import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { SelectOption } from 'components/Select'
import { useGetViewports, ViewportSlice } from 'features/globalSetup/features/viewport'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { MediaTypeRefresherViewportAttributeSchema, Schema } from '../../../../schema'
import { UseRefreshSizeViewports, ViewportOptionSlice } from './types'

export const useRefreshSizeViewports = (): UseRefreshSizeViewports => {
  const [viewports, setViewports] = useState<ViewportSlice[]>([])

  const { workspaceId } = useWorkspaceParam()
  const { lazyGetViewports } = useGetViewports({ workspaceId })

  const fetchViewports = async (): Promise<void> => {
    const viewports = await lazyGetViewports()
    setViewports(viewports)
  }

  useEffect(() => {
    fetchViewports()
  }, [])

  const { watch } = useFormContext<Schema>()
  const {
    refreshRedirect: {
      mediaTypeRefreshers: { newViewportId, viewportAttributes },
    },
  } = watch()

  const activeViewports = viewportAttributes.map(
    ({ viewport }: MediaTypeRefresherViewportAttributeSchema) => viewport,
  )

  const inactiveViewports: ViewportSlice[] = _.differenceBy(
    viewports,
    activeViewports,
    'viewportId',
  )

  const options: SelectOption<ViewportOptionSlice>[] = inactiveViewports.map(
    ({ name, viewportId }: ViewportSlice): SelectOption<ViewportOptionSlice> => ({
      label: name,
      value: viewportId,
    }),
  )

  const getNewViewportData: UseRefreshSizeViewports['getNewViewportData'] = () => {
    const template = inactiveViewports.find(
      (viewport: ViewportSlice): boolean => viewport.viewportId === newViewportId,
    )

    if (template === undefined) {
      return null
    }

    const schema: MediaTypeRefresherViewportAttributeSchema = {
      additionalSizes: [],
      removeSizes: [],
      updatedAt: new Date(),
      viewport: {
        minimumWidth: template.minimumWidth,
        name: template.name,
        viewportId: template.viewportId,
      },
      viewportId: template.viewportId,
    }

    const position: number = _.sortedIndexBy(activeViewports, template, 'minimumWidth')

    return [position, schema]
  }

  return { getNewViewportData, options }
}
